import { useEffect, useState } from "react";
import {
  useTable as useTanstackTable,
  type ExpandedState,
  type OnChangeFn,
  type RowSelectionState,
  type ColumnVisibilityState,
} from "@tanstack/react-table";
import { type SearchProductWithScore } from "~/hooks/search/types";
import { type ColumnDef } from "~/components/table";
import {
  appTableFeatures,
  type AppFeatures,
  type Table,
} from "~/shared/react-table/app-table";

interface UseHybridBooksTableArgs {
  data: SearchProductWithScore[];
  columns: ColumnDef<SearchProductWithScore>[];
  totalRecords: number;
  resetSelectionKey: string;
  columnVisibility?: ColumnVisibilityState;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

interface UseHybridBooksTableResult {
  table: Table<SearchProductWithScore>;
}

export const useHybridBooksTable = ({
  data,
  columns,
  totalRecords,
  resetSelectionKey,
  columnVisibility,
  rowSelection,
  setRowSelection,
}: UseHybridBooksTableArgs): UseHybridBooksTableResult => {
  const [editing, setEditing] = useState({});
  const [selectedAll, setSelectedAll] = useState(false);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // resetSelectionKey is derived upstream, so there is no event handler to clear the selection from.
  useEffect(() => {
    setRowSelection({});
    setSelectedAll(false);
  }, [resetSelectionKey, setRowSelection]);

  const table = useTanstackTable<AppFeatures, SearchProductWithScore>({
    features: appTableFeatures,
    data,
    columns,
    state: {
      rowSelection,
      editing,
      selectedAll,
      columnVisibility,
      expanded,
    },
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount: totalRecords,
    enableRowSelection: true,
    enableSorting: false,
    enableExpanding: true,
    getRowCanExpand: () => true,
    autoResetExpanded: false,
    onRowSelectionChange: setRowSelection,
    onEditChange: setEditing,
    onSelectedAllChange: setSelectedAll,
    onExpandedChange: setExpanded,
  });

  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
  useEffect(() => {
    if (!isAllPageRowsSelected && selectedAll) {
      // drops the cross-page "all results selected" flag once the page selection breaks; the flag follows table-owned selection state, so there is no event handler to reset it from
      setSelectedAll(false);
    }
  }, [isAllPageRowsSelected, selectedAll]);

  return { table: table as Table<SearchProductWithScore> };
};
